<template>
  <v-row>
    <v-col cols="12" md="4">
      <form novalidate @submit.prevent="submit">
        <p class="caption">
          Note: Results will shown as per
          <strong class="red--text">{{ formData.budgetYear.text || 'NA' }}</strong>
        </p>
        <v-select
          :items="budgetYearValues"
          v-model="formData.budgetYear"
          @change="renderResults"
          v-validate="'required'"
          :error-messages="errors.collect('budgetYear')"
          :label="dictionary.attributes.budgetYear"
          data-vv-name="budgetYear"
          :return-object="true"
          required
        ></v-select>
        <v-radio-group v-model="formData.employmentType"
          label="Income Type" row
          @change="renderResults">
          <v-radio
            v-for="(type, index) in employmentTypesList"
            :key="index"
            :label="type.label"
            :value="type.label"
          ></v-radio>
        </v-radio-group>
        <v-select
          v-if="isEmployee"
          :items="ageGroupValues"
          v-model="formData.ageGroup"
          @change="renderResults"
          v-validate="'required'"
          :error-messages="errors.collect('ageGroup')"
          :label="dictionary.attributes.ageGroup"
          data-vv-name="ageGroup"
          :return-object="true"
          required
        ></v-select>
        <v-text-field
          v-model="formData.totalIncome"
          @input="renderResults"
          type="number" step="5000"
          v-validate="'required|numeric'"
          :error-messages="errors.collect('totalIncome')"
          :label="dictionary.attributes.totalIncome"
          data-vv-name="totalIncome"
          required
        ></v-text-field>
        <v-text-field
          v-model="formData.totalDeduction"
          @input="renderResults"
          type="number" step="5000"
          v-validate="'required|numeric'"
          :error-messages="errors.collect('totalDeduction')"
          :label="dictionary.attributes.totalDeduction"
          data-vv-name="totalDeduction"
          required
        ></v-text-field>
        <div class="mt-5 d-flex">
          <v-spacer></v-spacer>
          <span v-if="!saving">
            <v-btn depressed text @click="reset">Reset</v-btn>
            <v-btn depressed color="accent" class="ml-2" type="submit">Calculate</v-btn>
          </span>
          <v-btn depressed color="accent" class="ml-2" disabled v-if="saving">
            <v-progress-circular indeterminate
              size="12" width="1"
              class="mr-1"></v-progress-circular>
            <span>Saving...</span>
          </v-btn>
        </div>
      </form>
    </v-col>
    <v-col cols="12" md="8">
      <v-card tile flat>
        <v-row no-gutters>
          <v-col cols="6" sm="6">
            <v-card tile elevation="0">
              <v-card flat height="120">
                <v-card-text>
                  <h4 class="overline mb-3">{{ cardTitle }}</h4>
                </v-card-text>
              </v-card>
                <div v-for="(item, key) in resultAttributes"
                    :key="key">
                  <v-divider></v-divider>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title class="body-2">{{ item }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="6"
            v-for="(group, index) in resultGroups"
            :key="index">
              <v-card tile :elevation="group.active?1:0" height="100%"
              :color="group.active?'light-blue lighten-5':''">
                <v-card tile flat height="120"
                  :dark="group.active"
                  :color="group.active?'accent':''">
                <v-card-text>
                  <h4 class="overline mb-3">{{ group.title }}</h4>
                  <div class="headline font-weight-bold">{{ group.tax | currency }}</div>
                  <div>finacial year {{ formData.budgetYear.text || 'NA' }}</div>
                </v-card-text>
                </v-card>
                <div v-for="(item, key) in resultAttributes"
                    :key="key">
                  <v-divider></v-divider>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title v-if="key==='taxPercent' || key==='cessPercent'"
                        class="body-2">
                        {{ (group[key] || 0)*100 }}%
                      </v-list-item-title>
                      <v-list-item-title v-else
                        class="body-2">
                        {{ group[key] || 0 | currency }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </div>
              </v-card>
            </v-col>
        </v-row>
      </v-card>
    </v-col>
  </v-row>
</template>
<style scoped>
.elevation-1 {
  z-index: 1;
  transform: scale(1.05);
}
</style>
<script>
/* eslint-disable prefer-destructuring */
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'tax-calculator-tool',

  $_veeValidate: {
    validator: 'new',
  },

  data: () => ({
    saving: false,
    dictionary: {
      attributes: {
        totalIncome: 'Total Income',
        totalDeduction: 'Other Deductions',
        budgetYear: 'Financial Year',
        ageGroup: 'Age Group',
      },
    },
    formData: {},
    resultGroups: [
      {
        id: 'result',
        title: 'Tax Payable',
        active: true,
      },
    ],

  }),

  computed: {
    ...mapState('tax', {
      cardTitle: state => state.taxCalculator.cardTitle,
      resultAttributes: state => state.taxCalculator.resultAttributes,
      formDefaults: state => state.taxCalculator.formDefaults,
      employmentTypesList: state => state.taxCalculator.employmentTypesList,
      budgetYearValues: state => state.taxCalculator.budgetYearValues,
      ageGroupValues: state => state.taxCalculator.ageGroupValues,
    }),
    ...mapGetters('tax', [
      'taxBreakup',
    ]),
    isEmployee() {
      return this.formData.employmentType.toLowerCase() === 'employee';
    },
  },

  mounted() {
    this.$validator.localize('en', this.dictionary);
    this.reset();
  },

  methods: {
    submit() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.renderResults();
        }
      });
    },
    reset() {
      this.formData = {
        ...this.formDefaults,
      };
      this.clear();
      this.renderResults();
    },
    clear() {
      this.$validator.reset();
    },
    getTaxBreakup(totalIncome, standardDeduction, exempSalary, taxPercent, excValue) {
      const cessPercent = 0.04;
      const taxableAmount = totalIncome - standardDeduction - exempSalary;
      const taxExclCess = excValue + (taxPercent * taxableAmount);
      const tax = taxExclCess + (cessPercent * taxExclCess);
      return {
        cessPercent,
        tax,
        taxExclCess,
      };
    },
    renderResults() {
      let {
        totalIncome, totalDeduction,
      } = this.formData;

      const { budgetYear, ageGroup } = this.formData;

      if (totalIncome === '' || totalDeduction === '') {
        return;
      }

      totalIncome = +totalIncome;
      totalDeduction = +totalDeduction;

      this.resultGroups.forEach((item, index) => {
        const payload = this.taxBreakup({
          budgetYear: budgetYear.value,
          ageGroup: ageGroup.value,
          totalIncome,
          totalDeduction,
          isEmployee: this.isEmployee,
        });
        const currentItem = {
          ...item,
          ...this.formData,
          ...payload,
        };

        this.$set(this.resultGroups, index, currentItem);
      });
    },
  },
};
</script>
